import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { personAppearanceKey } from '../shared/person-appearance-key';
import { personsViewMessages } from './persons-view-messages';
import { PersonsViewAppearanceRow } from './PersonsViewAppearanceRow';

const LIST_MAX_HEIGHT_PX = 280;

type PersonsViewAppearanceListProps = {
  lane: PersonLane;
  appearances: PersonAppearance[];
  hoveredKey: string | null;
  onHoverKey: (key: string | null) => void;
};

/**
 * Scrollable appearance table body under the Personas detail timeline.
 *
 * Column headers + capped list so a busy person does not expand the results
 * tab unboundedly. Hover syncs with {@link LaneMiniTimeline}.
 */
export function PersonsViewAppearanceList({
  lane,
  appearances,
  hoveredKey,
  onHoverKey,
}: PersonsViewAppearanceListProps) {
  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
        <FormattedMessage {...personsViewMessages.appearancesDetail} />
      </Typography>
      <Box
        sx={{
          maxHeight: LIST_MAX_HEIGHT_PX,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            // Opaque so scrolled rows do not show through the sticky header.
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>
            <FormattedMessage id="common.text.time" defaultMessage="Hora" />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
            <FormattedMessage {...personsViewMessages.sources} />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
            <FormattedMessage id="common.text.duration" defaultMessage="Duración" />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
            <FormattedMessage {...personsViewMessages.confidence} />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            <FormattedMessage {...personsViewMessages.actions} />
          </Typography>
        </Stack>
        <Stack spacing={0} sx={{ py: 0.5 }}>
          {appearances.map((a) => {
            const key = personAppearanceKey(a);
            return (
              <PersonsViewAppearanceRow
                key={key}
                lane={lane}
                appearance={a}
                highlighted={hoveredKey === key}
                onHover={() => onHoverKey(key)}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
